import { ORGANIZATION_LABELS, type Catalog, type CatalogCard } from '@/types/card'
import { normalizeForSubstring } from './normalize'

/**
 * Facetten — Kategorie, Schlagwort und Organisation als anklickbare Filter.
 *
 * Bewusst **kein** Teil der Suchmaschine und damit nicht im Worker: Ein Vorschlag
 * entsteht aus dem Vergleich einer Eingabe mit ein paar Dutzend Bezeichnungen, nicht aus
 * dem Volltext. Das kostet Mikrosekunden und braucht weder den Suchindex noch einen
 * zweiten Thread. Im Worker hätte es nur eine Anfrageart, ein Token und eine Wartezeit
 * mehr bedeutet — bei gleichem Ergebnis.
 *
 * Was hier dagegen **gemeinsam** mit der Suchmaschine liegt, ist `passtZuFiltern`: Die
 * Entscheidung, ob eine Karte zu den gesetzten Filtern passt, darf es nur einmal geben.
 * Sonst zeigte die Trefferliste etwas anderes an als der Vorschlag versprochen hat.
 */

export interface SearchFilters {
  organizations?: readonly string[]
  categories?: readonly string[]
  tags?: readonly string[]
}

/** Woraus ein Filter gebildet wurde. Bestimmt zugleich die Gruppe im Vorschlagsblock. */
export type FacetKind = 'kategorie' | 'schlagwort' | 'organisation'

/**
 * Ein Filter, gesetzt oder vorgeschlagen.
 *
 * Die Kennung ist nur innerhalb ihrer Art eindeutig — „gefahrgut" gibt es sowohl als
 * Kategorie als auch als Schlagwort. Wer Facetten vergleicht oder ablegt, muss deshalb
 * die Art mitführen; `facetKey` bildet beides auf einen Schlüssel ab.
 */
export interface Facet {
  kind: FacetKind
  id: string
  label: string
}

export function facetKey(facet: Pick<Facet, 'kind' | 'id'>): string {
  return `${facet.kind}:${facet.id}`
}

/** Beschriftung der Gruppen im Vorschlagsblock, Einzahl — es steht je Zeile eine Facette. */
export const FACET_KIND_LABELS: Record<FacetKind, string> = {
  kategorie: 'Kategorie',
  schlagwort: 'Schlagwort',
  organisation: 'Organisation',
}

/** Reihenfolge der Gruppen: vom Groben zum Feinen. */
export const FACET_KINDS: readonly FacetKind[] = ['kategorie', 'schlagwort', 'organisation']

/** Höchstens so viele Vorschläge je Gruppe — mehr liest im Einsatz niemand. */
const FACET_LIMIT = 4

/** Ab dieser Eingabelänge wird vorgeschlagen. Darunter passt fast alles auf fast alles. */
const FACET_MIN_QUERY = 2

/** Eine Facette samt vorbereitetem Suchtext. Entsteht einmal je Katalog. */
export interface FacetIndex {
  facet: Facet
  /** Klartext und Kennung, normalisiert — beides wird durchsucht. */
  normalized: string
}

/**
 * Alle Facetten des Bestands, einmal aufbereitet.
 *
 * Nur was tatsächlich an einer Karte hängt: Ein Vorschlag, der zu null Treffern führt,
 * ist im Einsatz schlimmer als gar kein Vorschlag.
 *
 * Die Kennung wandert mit in den Suchtext. Wer „fwdv" tippt, meint das Schlagwort
 * „fwdv-7", auch wenn dessen Klartext „FwDV 7" die Zeichen anders anordnet.
 */
export function baueFacetten(catalog: Catalog): FacetIndex[] {
  const cards = catalog.cards
  const vorhandeneKategorien = new Set(cards.map((card) => card.category))
  const vorhandeneSchlagworte = new Set(cards.flatMap((card) => card.tags))
  const vorhandeneOrganisationen = new Set(cards.map((card) => card.organization))

  const facetten: Facet[] = [
    ...catalog.categories
      .filter((category) => vorhandeneKategorien.has(category.id))
      .map((category): Facet => ({ kind: 'kategorie', id: category.id, label: category.label })),
    ...catalog.tags
      .filter((tag) => vorhandeneSchlagworte.has(tag.id))
      .map((tag): Facet => ({ kind: 'schlagwort', id: tag.id, label: tag.label })),
    ...[...vorhandeneOrganisationen].map(
      (org): Facet => ({ kind: 'organisation', id: org, label: ORGANIZATION_LABELS[org] }),
    ),
  ]

  return facetten.map((facet) => ({
    facet,
    normalized: `${normalizeForSubstring(facet.label)} ${normalizeForSubstring(facet.id)}`,
  }))
}

/**
 * Passt eine Karte zu den gesetzten Filtern?
 *
 * Organisation und Kategorie wirken als Oder innerhalb ihrer Art: Wer Feuerwehr und THW
 * wählt, will beides sehen. Schlagwörter wirken dagegen als Und — eine Karte kann
 * mehrere tragen, und wer zwei auswählt, meint die Schnittmenge. Wäre es auch dort ein
 * Oder, vergrößerte jede weitere Auswahl das Ergebnis, und der Filter liefe seinem
 * Zweck zuwider.
 */
export function passtZuFiltern(card: CatalogCard, filters: SearchFilters): boolean {
  if (filters.organizations?.length && !filters.organizations.includes(card.organization)) {
    return false
  }
  if (filters.categories?.length && !filters.categories.includes(card.category)) return false
  if (filters.tags?.length && !filters.tags.every((tag) => card.tags.includes(tag))) return false
  return true
}

/** Ist überhaupt ein Filter gesetzt? */
export function hatFilter(filters: SearchFilters): boolean {
  return Boolean(
    filters.organizations?.length || filters.categories?.length || filters.tags?.length,
  )
}

/** Die gesetzten Filter als Facetten-Schlüssel — für den Abgleich mit den Vorschlägen. */
function gesetzteSchluessel(filters: SearchFilters): Set<string> {
  return new Set([
    ...(filters.categories ?? []).map((id) => facetKey({ kind: 'kategorie', id })),
    ...(filters.tags ?? []).map((id) => facetKey({ kind: 'schlagwort', id })),
    ...(filters.organizations ?? []).map((id) => facetKey({ kind: 'organisation', id })),
  ])
}

/**
 * Filtervorschläge zur Eingabe.
 *
 * Verglichen wird gegen Klartext und Kennung der Facette, nicht gegen den Inhalt der
 * Karten: Wer „gefahr" tippt, bekommt die Kategorie „Gefahrgut & ABC" und das Schlagwort
 * „Gefahrgut" angeboten — unabhängig davon, wie viele Karten das Wort irgendwo im
 * Fließtext führen.
 *
 * Heraus fallen bereits gesetzte Filter und solche, die zusammen mit den gesetzten keine
 * einzige Karte übrig ließen.
 */
export function schlageFacettenVor(
  index: readonly FacetIndex[],
  cards: readonly CatalogCard[],
  query: string,
  filters: SearchFilters,
): Facet[] {
  const needle = normalizeForSubstring(query.trim())
  if (needle.length < FACET_MIN_QUERY) return []

  const gesetzt = gesetzteSchluessel(filters)
  // Einmal vorab: Welche Karten kommen unter den gesetzten Filtern überhaupt noch in
  // Frage? Ohne das liefe die Prüfung je Vorschlag erneut über den ganzen Bestand.
  const moegliche = cards.filter((card) => passtZuFiltern(card, filters))

  const treffer: { facet: Facet; rang: number }[] = []
  for (const { facet, normalized } of index) {
    if (gesetzt.has(facetKey(facet))) continue
    const at = normalized.indexOf(needle)
    if (at === -1) continue
    if (!moegliche.some((card) => traegt(card, facet))) continue
    // Ein Treffer am Wortanfang wiegt schwerer als einer mitten im Wort: „funk" meint
    // eher „Funkrufnamen" als „Digitalfunk".
    treffer.push({ facet, rang: at === 0 || normalized[at - 1] === ' ' ? 0 : 1 })
  }

  treffer.sort((a, b) => a.rang - b.rang || a.facet.label.localeCompare(b.facet.label, 'de'))

  const proArt = new Map<FacetKind, number>()
  return treffer.flatMap(({ facet }) => {
    const bisher = proArt.get(facet.kind) ?? 0
    if (bisher >= FACET_LIMIT) return []
    proArt.set(facet.kind, bisher + 1)
    return [facet]
  })
}

function traegt(card: CatalogCard, facet: Facet): boolean {
  if (facet.kind === 'kategorie') return card.category === facet.id
  if (facet.kind === 'schlagwort') return card.tags.includes(facet.id)
  return card.organization === facet.id
}

/** Fügt eine Facette den Filtern hinzu — oder nimmt sie wieder heraus. */
export function schalteFacette(filters: SearchFilters, facet: Facet): SearchFilters {
  const feld =
    facet.kind === 'kategorie'
      ? 'categories'
      : facet.kind === 'schlagwort'
        ? 'tags'
        : 'organizations'
  const bisher = filters[feld] ?? []
  const next = bisher.includes(facet.id)
    ? bisher.filter((id) => id !== facet.id)
    : [...bisher, facet.id]
  return { ...filters, [feld]: next }
}

/** Die gesetzten Filter als Facetten, in stabiler Reihenfolge — für die Plättchen im Feld. */
export function gesetzteFacetten(
  index: readonly FacetIndex[],
  filters: SearchFilters,
): Facet[] {
  const gesetzt = gesetzteSchluessel(filters)
  return index.map((eintrag) => eintrag.facet).filter((facet) => gesetzt.has(facetKey(facet)))
}
